import apiClient from '@/lib/api/client';
import { useOrganizationsStore } from '@/stores/organizations';
import type {
  OrgBaseModelResponse,
  AddBaseModelRequest,
  ListBaseModelsResponse,
  ListOrgBaseModelsResponse,
  CustomModelResponse,
  CreateCustomModelRequest,
  UpdateCustomModelRequest,
  ListCustomModelsResponse,
  AsabModelResponse,
  CreateAsabModelRequest,
  UpdateAsabModelRequest,
  ListAsabModelsResponse,
  BaseModelQueryParams,
  CustomModelQueryParams,
  AsabModelQueryParams,
  CustomModelConnectionResponse,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  ListConnectionsResponse,
  NormalisedModelsResponse,
  ImportRequest,
  ImportResponse,
} from '../types';

/** Returns the org-scoped base path for model gateway endpoints. */
function orgPath(): string {
  const orgStore = useOrganizationsStore();
  const orgId = orgStore.currentOrganizationId;
  if (!orgId) throw new Error('No organization selected');
  return `/v1/organizations/${orgId}`;
}

/**
 * Normalise a raw org-base-model object from the gateway into the
 * shape the frontend components expect (BaseModelResponse).
 *
 * The gateway may return the enriched shape (slug, is_enabled_for_org, …)
 * or the raw OrgBaseModel shape (base_model_slug, is_enabled, …).
 * This function handles both so the rest of the frontend doesn't have to care.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normaliseBaseModel(raw: any): any {
  return {
    ...raw,
    // slug: prefer slug, fall back to base_model_slug
    slug: raw.slug ?? raw.base_model_slug ?? '',
    // is_enabled_for_org: prefer enriched field, fall back to is_enabled
    is_enabled_for_org: raw.is_enabled_for_org ?? raw.is_enabled ?? false,
    is_added_to_org: raw.is_added_to_org ?? true,
    // Pricing — default to 0 so .toFixed() never crashes
    input_cost_1m: raw.input_cost_1m ?? 0,
    output_cost_1m: raw.output_cost_1m ?? 0,
    // Other fields that may be absent on the raw shape
    provider: raw.provider ?? '',
    input_modalities: raw.input_modalities ?? [],
    output_modalities: raw.output_modalities ?? [],
    supported_parameters: raw.supported_parameters ?? [],
    context_window: raw.context_window ?? 0,
    max_output_tokens: raw.max_output_tokens ?? 0,
  };
}

/**
 * Model Gateway API client
 * Provides functions for managing base models, custom models (BYOM), and ASAB models
 */
export const modelGatewayApi = {
  // =============================================================================
  // Base Model Operations
  // =============================================================================

  /**
   * List all available base models from provider registry with optional filtering
   * @param params - Optional query parameters for filtering
   * @returns Promise with list of base models
   */
  listBaseModels: async (params?: BaseModelQueryParams): Promise<ListBaseModelsResponse> => {
    const response = await apiClient.get<ListBaseModelsResponse>(`${orgPath()}/base-models`, {
      params,
    });
    const data = response.data;
    return {
      ...data,
      data: (data.data ?? []).map(normaliseBaseModel),
    };
  },

  /**
   * List enabled base models for the organization
   * (Filters list client-side — gateway returns all org models via GET /)
   * @returns Promise with list of enabled base models
   */
  listEnabledBaseModels: async (): Promise<ListOrgBaseModelsResponse> => {
    const response = await apiClient.get<ListOrgBaseModelsResponse>(`${orgPath()}/base-models`);
    return response.data;
  },

  /**
   * Get details for a specific base model by name
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise with base model details
   */
  getBaseModel: async (name: string): Promise<OrgBaseModelResponse> => {
    const response = await apiClient.get<OrgBaseModelResponse>(
      `${orgPath()}/base-models/${name}`
    );
    return response.data;
  },

  /**
   * Add a base model to the organization with optional custom name
   * @param data - Request containing base model slug and optional custom name
   * @returns Promise with organization base model response
   */
  addBaseModel: async (data: AddBaseModelRequest): Promise<OrgBaseModelResponse> => {
    const response = await apiClient.post<OrgBaseModelResponse>(
      `${orgPath()}/base-models`,
      data
    );
    return response.data;
  },

  /**
   * Enable a base model for the organization
   * @param name - The name of the base model
   * @returns Promise with organization base model response
   */
  enableBaseModel: async (name: string): Promise<OrgBaseModelResponse> => {
    const response = await apiClient.put<OrgBaseModelResponse>(
      `${orgPath()}/base-models/${name}`,
      { is_enabled: true }
    );
    return response.data;
  },

  /**
   * Disable a base model for the organization
   * @param name - The name of the base model
   * @returns Promise with organization base model response
   */
  disableBaseModel: async (name: string): Promise<OrgBaseModelResponse> => {
    const response = await apiClient.put<OrgBaseModelResponse>(
      `${orgPath()}/base-models/${name}`,
      { is_enabled: false }
    );
    return response.data;
  },

  /**
   * Remove a base model from the organization
   * @param name - The name of the base model
   * @returns Promise that resolves when deletion is complete
   */
  deleteBaseModel: async (name: string): Promise<void> => {
    await apiClient.delete(`${orgPath()}/base-models/${name}`);
  },

  // =============================================================================
  // Custom Model Operations (BYOM - Bring Your Own Model)
  // =============================================================================

  /**
   * List all custom models for the organization
   * @param params - Optional query parameters for filtering
   * @returns Promise with list of custom models
   */
  listCustomModels: async (params?: CustomModelQueryParams): Promise<ListCustomModelsResponse> => {
    const response = await apiClient.get<ListCustomModelsResponse>(
      `${orgPath()}/custom-models`,
      { params }
    );
    return response.data;
  },

  /**
   * Get details for a specific custom model by name
   * @param name - The name of the custom model
   * @returns Promise with custom model details
   */
  getCustomModel: async (name: string): Promise<CustomModelResponse> => {
    const response = await apiClient.get<CustomModelResponse>(
      `${orgPath()}/custom-models/${name}`
    );
    return response.data;
  },

  /**
   * Create a new custom model (BYOM)
   * @param data - Request containing custom model configuration
   * @returns Promise with created custom model
   */
  createCustomModel: async (data: CreateCustomModelRequest): Promise<CustomModelResponse> => {
    const response = await apiClient.post<CustomModelResponse>(
      `${orgPath()}/custom-models`,
      data
    );
    return response.data;
  },

  /**
   * Update an existing custom model
   * @param name - The name of the custom model
   * @param data - Request containing updated custom model configuration
   * @returns Promise with updated custom model
   */
  updateCustomModel: async (
    name: string,
    data: UpdateCustomModelRequest
  ): Promise<CustomModelResponse> => {
    const response = await apiClient.put<CustomModelResponse>(
      `${orgPath()}/custom-models/${name}`,
      data
    );
    return response.data;
  },

  /**
   * Enable a custom model
   * @param name - The name of the custom model
   * @returns Promise with updated custom model
   */
  enableCustomModel: async (name: string): Promise<CustomModelResponse> => {
    const response = await apiClient.put<CustomModelResponse>(
      `${orgPath()}/custom-models/${name}`,
      { is_enabled: true }
    );
    return response.data;
  },

  /**
   * Disable a custom model
   * @param name - The name of the custom model
   * @returns Promise with updated custom model
   */
  disableCustomModel: async (name: string): Promise<CustomModelResponse> => {
    const response = await apiClient.put<CustomModelResponse>(
      `${orgPath()}/custom-models/${name}`,
      { is_enabled: false }
    );
    return response.data;
  },

  /**
   * Delete a custom model
   * @param name - The name of the custom model
   * @returns Promise that resolves when deletion is complete
   */
  deleteCustomModel: async (name: string): Promise<void> => {
    await apiClient.delete(`${orgPath()}/custom-models/${name}`);
  },

  // =============================================================================
  // ASAB Model Operations (Augmented Service Access Bridge)
  // =============================================================================

  /**
   * List all ASAB models for the organization
   * @param params - Optional query parameters for filtering
   * @returns Promise with list of ASAB models
   */
  listAsabModels: async (params?: AsabModelQueryParams): Promise<ListAsabModelsResponse> => {
    const response = await apiClient.get<ListAsabModelsResponse>(
      `${orgPath()}/asab-models`,
      { params }
    );
    return response.data;
  },

  /**
   * Get details for a specific ASAB model by name
   * @param name - The name of the ASAB model
   * @returns Promise with ASAB model details
   */
  getAsabModel: async (name: string): Promise<AsabModelResponse> => {
    const response = await apiClient.get<AsabModelResponse>(
      `${orgPath()}/asab-models/${name}`
    );
    return response.data;
  },

  /**
   * Create a new ASAB model with routing configuration
   * @param data - Request containing ASAB model configuration
   * @returns Promise with created ASAB model
   */
  createAsabModel: async (data: CreateAsabModelRequest): Promise<AsabModelResponse> => {
    const response = await apiClient.post<AsabModelResponse>(
      `${orgPath()}/asab-models`,
      data
    );
    return response.data;
  },

  /**
   * Update an existing ASAB model
   * @param name - The name of the ASAB model
   * @param data - Request containing updated ASAB model configuration
   * @returns Promise with updated ASAB model
   */
  updateAsabModel: async (
    name: string,
    data: UpdateAsabModelRequest
  ): Promise<AsabModelResponse> => {
    const response = await apiClient.put<AsabModelResponse>(
      `${orgPath()}/asab-models/${name}`,
      data
    );
    return response.data;
  },

  /**
   * Enable an ASAB model
   * @param name - The name of the ASAB model
   * @returns Promise with updated ASAB model
   */
  enableAsabModel: async (name: string): Promise<AsabModelResponse> => {
    const response = await apiClient.put<AsabModelResponse>(
      `${orgPath()}/asab-models/${name}`,
      { is_enabled: true }
    );
    return response.data;
  },

  /**
   * Disable an ASAB model
   * @param name - The name of the ASAB model
   * @returns Promise with updated ASAB model
   */
  disableAsabModel: async (name: string): Promise<AsabModelResponse> => {
    const response = await apiClient.put<AsabModelResponse>(
      `${orgPath()}/asab-models/${name}`,
      { is_enabled: false }
    );
    return response.data;
  },

  /**
   * Delete an ASAB model
   * @param name - The name of the ASAB model
   * @returns Promise that resolves when deletion is complete
   */
  deleteAsabModel: async (name: string): Promise<void> => {
    await apiClient.delete(`${orgPath()}/asab-models/${name}`);
  },

  // =============================================================================
  // Custom Model Connection Operations (BYOM — connection-based listing)
  // =============================================================================

  listConnections: async (): Promise<ListConnectionsResponse> => {
    const response = await apiClient.get<ListConnectionsResponse>(
      `${orgPath()}/connections`
    );
    return response.data;
  },

  getConnection: async (slug: string): Promise<CustomModelConnectionResponse> => {
    const response = await apiClient.get<CustomModelConnectionResponse>(
      `${orgPath()}/connections/${slug}`
    );
    return response.data;
  },

  createConnection: async (data: CreateConnectionRequest): Promise<CustomModelConnectionResponse> => {
    const response = await apiClient.post<CustomModelConnectionResponse>(
      `${orgPath()}/connections`,
      data
    );
    return response.data;
  },

  updateConnection: async (slug: string, data: UpdateConnectionRequest): Promise<CustomModelConnectionResponse> => {
    const response = await apiClient.put<CustomModelConnectionResponse>(
      `${orgPath()}/connections/${slug}`,
      data
    );
    return response.data;
  },

  deleteConnection: async (slug: string): Promise<void> => {
    await apiClient.delete(`${orgPath()}/connections/${slug}`);
  },

  /** List upstream models available at a connection's endpoint */
  listConnectionModels: async (slug: string): Promise<NormalisedModelsResponse> => {
    const response = await apiClient.get<NormalisedModelsResponse>(
      `${orgPath()}/connections/${slug}/models`
    );
    return response.data;
  },

  /** Import selected upstream models as custom models */
  importConnectionModels: async (slug: string, data: ImportRequest): Promise<ImportResponse> => {
    const response = await apiClient.post<ImportResponse>(
      `${orgPath()}/connections/${slug}/import`,
      data
    );
    return response.data;
  },
};
